import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Checkbox } from './ui/checkbox';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import { Plus, Trash2, Edit, Save, X, ChevronDown, ChevronRight, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const getToken = () => localStorage.getItem('token');

const api = async (endpoint, options = {}) => {
  const token = getToken();
  const headers = {
    'Content-Type': 'application/json',
    ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
    ...options.headers
  };
  const response = await fetch(`${API_URL}${endpoint}`, { ...options, headers });
  if (!response.ok) throw new Error(await response.text());
  return response.json();
};

export function ZoneTaskMatrixEditor({ walkthroughId, buildingType = 'office', onUpdate }) {
  const [zones, setZones] = useState([]);
  const [expandedZones, setExpandedZones] = useState(new Set());
  const [editingZone, setEditingZone] = useState(null);
  const [editingTask, setEditingTask] = useState(null);
  const [newZone, setNewZone] = useState({ zone_name: '', zone_type: 'office', service_frequency: 'nightly', zone_notes: '' });
  const [newTask, setNewTask] = useState({ task_name: '', nightly: false, weekly: false, monthly: false });
  const [addingZone, setAddingZone] = useState(false);
  const [addingTaskToZone, setAddingTaskToZone] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (walkthroughId) {
      console.log('[ZoneTaskMatrixEditor] Loading zones for walkthrough:', walkthroughId);
      loadZonesAndTasks();
      loadWalkthroughData();
    }
  }, [walkthroughId]);

  const loadWalkthroughData = async () => {
    try {
      const data = await api(`/api/walkthroughs/${walkthroughId}/zones-with-tasks`);
      setWalkthroughData(data);
    } catch (error) {
      console.error('Failed to load walkthrough data:', error);
    }
  };

  const loadZonesAndTasks = async () => {
    try {
      console.log('[ZoneTaskMatrixEditor] Fetching zones from API:', `/api/walkthroughs/${walkthroughId}/zones-with-tasks`);
      const data = await api(`/api/walkthroughs/${walkthroughId}/zones-with-tasks`);
      console.log('[ZoneTaskMatrixEditor] Received zones:', data.zones?.length || 0);
      setZones(data.zones || []);
      
      // Expand all zones by default
      if (data.zones && data.zones.length > 0) {
        setExpandedZones(new Set(data.zones.map(z => z.id)));
      }
    } catch (error) {
      console.error('Failed to load zones and tasks:', error);
      toast.error('Failed to load zones and tasks');
    }
  };

  const handleApplyTemplate = async () => {
    if (!window.confirm(`Apply ${buildingType} template? This will add default zones and tasks.`)) {
      return;
    }

    setLoading(true);
    try {
      await api(`/api/walkthroughs/${walkthroughId}/apply-template?building_type=${buildingType}`, {
        method: 'POST'
      });
      toast.success('Template applied successfully');
      await loadZonesAndTasks();
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error('Failed to apply template');
    } finally {
      setLoading(false);
    }
  };

  const toggleZoneExpanded = (zoneId) => {
    const newExpanded = new Set(expandedZones);
    if (newExpanded.has(zoneId)) {
      newExpanded.delete(zoneId);
    } else {
      newExpanded.add(zoneId);
    }
    setExpandedZones(newExpanded);
  };

  const handleCreateZone = async () => {
    if (!newZone.zone_name.trim()) {
      toast.error('Zone name is required');
      return;
    }

    try {
      const zone = await api('/api/walkthrough-zones', {
        method: 'POST',
        body: JSON.stringify({
          walkthrough_id: walkthroughId,
          ...newZone
        })
      });
      
      toast.success('Zone created');
      setNewZone({ zone_name: '', zone_type: 'office', service_frequency: 'nightly', zone_notes: '' });
      setAddingZone(false);
      await loadZonesAndTasks();
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error('Failed to create zone');
    }
  };

  const handleUpdateZone = async (zoneId, updates) => {
    try {
      await api(`/api/walkthrough-zones/${zoneId}`, {
        method: 'PUT',
        body: JSON.stringify(updates)
      });
      toast.success('Zone updated');
      setEditingZone(null);
      await loadZonesAndTasks();
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error('Failed to update zone');
    }
  };

  const handleDeleteZone = async (zoneId) => {
    if (!window.confirm('Delete this zone and all its tasks? This cannot be undone.')) {
      return;
    }

    try {
      await api(`/api/walkthrough-zones/${zoneId}`, {
        method: 'DELETE'
      });
      toast.success('Zone deleted');
      await loadZonesAndTasks();
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error('Failed to delete zone');
    }
  };

  const handleCreateTask = async (zoneId) => {
    if (!newTask.task_name.trim()) {
      toast.error('Task name is required');
      return;
    }

    try {
      await api('/api/cleaning-tasks', {
        method: 'POST',
        body: JSON.stringify({
          walkthrough_id: walkthroughId,
          zone_id: zoneId,
          ...newTask,
          sort_order: 999
        })
      });
      
      toast.success('Task created');
      setNewTask({ task_name: '', nightly: false, weekly: false, monthly: false });
      setAddingTaskToZone(null);
      await loadZonesAndTasks();
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error('Failed to create task');
    }
  };

  const handleUpdateTask = async (taskId, updates) => {
    try {
      await api(`/api/cleaning-tasks/${taskId}`, {
        method: 'PUT',
        body: JSON.stringify(updates)
      });
      toast.success('Task updated');
      setEditingTask(null);
      await loadZonesAndTasks();
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error('Failed to update task');
    }
  };

  const handleDeleteTask = async (taskId) => {
    if (!window.confirm('Delete this task?')) {
      return;
    }

    try {
      await api(`/api/cleaning-tasks/${taskId}`, {
        method: 'DELETE'
      });
      toast.success('Task deleted');
      await loadZonesAndTasks();
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error('Failed to delete task');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-2xl font-[Space_Grotesk] font-semibold">Zones & Tasks Matrix</h3>
          <p className="text-sm text-muted-foreground">Define cleaning areas and specific tasks per zone</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleApplyTemplate} disabled={loading} data-testid="apply-template-button">
            <Sparkles className="h-4 w-4 mr-2" />
            {loading ? 'Applying...' : `Apply ${buildingType} Template`}
          </Button>
          <Button onClick={() => setAddingZone(true)} data-testid="add-zone-button">
            <Plus className="h-4 w-4 mr-2" />
            Add Zone
          </Button>
        </div>
      </div>

      {/* Add New Zone Form */}
      {addingZone && (
        <Card className="border-teal-200 border-2">
          <CardHeader>
            <CardTitle>New Zone</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Zone Name *</Label>
                <Input
                  value={newZone.zone_name}
                  onChange={(e) => setNewZone({...newZone, zone_name: e.target.value})}
                  placeholder="Server Room / IT Areas"
                  data-testid="new-zone-name-input"
                />
              </div>
              <div>
                <Label>Zone Type</Label>
                <select
                  className="w-full h-10 px-3 rounded-md border border-gray-300"
                  value={newZone.zone_type}
                  onChange={(e) => setNewZone({...newZone, zone_type: e.target.value})}
                  data-testid="new-zone-type-select"
                >
                  <option value="lobby">Lobby / Common Area</option>
                  <option value="office">Office / Workspace</option>
                  <option value="breakroom">Breakroom / Kitchen</option>
                  <option value="restroom">Restroom</option>
                  <option value="warehouse">Warehouse / Shop</option>
                  <option value="specialty">Specialty</option>
                </select>
              </div>
            </div>
            <div>
              <Label>Service Frequency</Label>
              <select
                className="w-full h-10 px-3 rounded-md border border-gray-300"
                value={newZone.service_frequency}
                onChange={(e) => setNewZone({...newZone, service_frequency: e.target.value})}
                data-testid="new-zone-frequency-select"
              >
                <option value="nightly">Nightly</option>
                <option value="5x_week">5x per Week</option>
                <option value="3x_week">3x per Week</option>
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
              </select>
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea
                value={newZone.zone_notes}
                onChange={(e) => setNewZone({...newZone, zone_notes: e.target.value})}
                rows={2}
                placeholder="Special instructions for this zone..."
                data-testid="new-zone-notes-input"
              />
            </div>
            <div className="flex gap-2">
              <Button onClick={handleCreateZone} data-testid="save-new-zone-button">
                <Save className="h-4 w-4 mr-2" />
                Save Zone
              </Button>
              <Button variant="outline" onClick={() => setAddingZone(false)}>
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Zones List */}
      {zones.length === 0 && !addingZone && (
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-muted-foreground">No zones defined yet</p>
            <p className="text-sm text-muted-foreground mt-2">
              Click "Apply {buildingType} Template" to auto-generate zones or "Add Zone" to create manually
            </p>
          </CardContent>
        </Card>
      )}

      {zones.map((zone) => {
        const isExpanded = expandedZones.has(zone.id);
        const isEditing = editingZone === zone.id;
        
        return (
          <Card key={zone.id} className="overflow-hidden">
            <CardHeader className="bg-teal-50 cursor-pointer" onClick={() => toggleZoneExpanded(zone.id)}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {isExpanded ? <ChevronDown className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />}
                  <div>
                    <CardTitle className="text-lg text-teal-800">{zone.zone_name}</CardTitle>
                    <CardDescription>
                      <Badge variant="secondary" className="mr-2 capitalize">{zone.zone_type}</Badge>
                      <span className="text-xs capitalize">{zone.service_frequency.replace('_', ' ')}</span>
                      <span className="text-xs ml-2">• {zone.tasks?.length || 0} tasks</span>
                    </CardDescription>
                  </div>
                </div>
                <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setEditingZone(zone.id)}
                    data-testid={`edit-zone-${zone.id}`}
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDeleteZone(zone.id)}
                    className="text-destructive"
                    data-testid={`delete-zone-${zone.id}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardHeader>

            {isExpanded && (
              <CardContent className="pt-6">
                {/* Edit Zone Form */}
                {isEditing && (
                  <div className="mb-6 p-4 bg-gray-50 rounded-lg space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Zone Name</Label>
                        <Input
                          defaultValue={zone.zone_name}
                          id={`edit-zone-name-${zone.id}`}
                          data-testid={`edit-zone-name-${zone.id}`}
                        />
                      </div>
                      <div>
                        <Label>Service Frequency</Label>
                        <select
                          className="w-full h-10 px-3 rounded-md border border-gray-300"
                          defaultValue={zone.service_frequency}
                          id={`edit-zone-freq-${zone.id}`}
                          data-testid={`edit-zone-freq-${zone.id}`}
                        >
                          <option value="nightly">Nightly</option>
                          <option value="5x_week">5x per Week</option>
                          <option value="3x_week">3x per Week</option>
                          <option value="weekly">Weekly</option>
                          <option value="monthly">Monthly</option>
                        </select>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        onClick={() => {
                          const name = document.getElementById(`edit-zone-name-${zone.id}`).value;
                          const freq = document.getElementById(`edit-zone-freq-${zone.id}`).value;
                          handleUpdateZone(zone.id, { zone_name: name, service_frequency: freq });
                        }}
                        data-testid={`save-zone-${zone.id}`}
                      >
                        Save
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => setEditingZone(null)}>
                        Cancel
                      </Button>
                    </div>
                  </div>
                )}

                {/* Tasks Header */}
                <div className="grid grid-cols-[1fr,100px,100px,100px,80px] gap-2 mb-3 text-sm font-semibold text-muted-foreground">
                  <div>Task</div>
                  <div className="text-center">Nightly</div>
                  <div className="text-center">Weekly</div>
                  <div className="text-center">Monthly</div>
                  <div></div>
                </div>

                {/* Tasks List */}
                {zone.tasks && zone.tasks.length > 0 ? (
                  zone.tasks.map((task) => {
                    const isTaskEditing = editingTask === task.id;
                    
                    return (
                      <div key={task.id} className="grid grid-cols-[1fr,100px,100px,100px,80px] gap-2 items-center p-2 bg-gray-50 rounded mb-2">
                        <div className="text-sm">
                          {isTaskEditing ? (
                            <Input
                              defaultValue={task.task_name}
                              className="h-8"
                              id={`edit-task-name-${task.id}`}
                              data-testid={`edit-task-name-${task.id}`}
                            />
                          ) : (
                            task.task_name
                          )}
                        </div>
                        
                        <div className="flex justify-center">
                          {isTaskEditing ? (
                            <Checkbox
                              defaultChecked={task.nightly}
                              id={`edit-task-nightly-${task.id}`}
                              data-testid={`edit-task-nightly-${task.id}`}
                            />
                          ) : (
                            task.nightly && <span className="text-green-600">✓</span>
                          )}
                        </div>
                        
                        <div className="flex justify-center">
                          {isTaskEditing ? (
                            <Checkbox
                              defaultChecked={task.weekly}
                              id={`edit-task-weekly-${task.id}`}
                              data-testid={`edit-task-weekly-${task.id}`}
                            />
                          ) : (
                            task.weekly && <span className="text-green-600">✓</span>
                          )}
                        </div>
                        
                        <div className="flex justify-center">
                          {isTaskEditing ? (
                            <Checkbox
                              defaultChecked={task.monthly}
                              id={`edit-task-monthly-${task.id}`}
                              data-testid={`edit-task-monthly-${task.id}`}
                            />
                          ) : (
                            task.monthly && <span className="text-green-600">✓</span>
                          )}
                        </div>
                        
                        <div className="flex gap-1">
                          {isTaskEditing ? (
                            <>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 w-7 p-0"
                                onClick={() => {
                                  const name = document.getElementById(`edit-task-name-${task.id}`).value;
                                  const nightly = document.getElementById(`edit-task-nightly-${task.id}`).checked;
                                  const weekly = document.getElementById(`edit-task-weekly-${task.id}`).checked;
                                  const monthly = document.getElementById(`edit-task-monthly-${task.id}`).checked;
                                  handleUpdateTask(task.id, { task_name: name, nightly, weekly, monthly });
                                }}
                                data-testid={`save-task-${task.id}`}
                              >
                                <Save className="h-3 w-3" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 w-7 p-0"
                                onClick={() => setEditingTask(null)}
                              >
                                <X className="h-3 w-3" />
                              </Button>
                            </>
                          ) : (
                            <>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 w-7 p-0"
                                onClick={() => setEditingTask(task.id)}
                                data-testid={`edit-task-${task.id}`}
                              >
                                <Edit className="h-3 w-3" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 w-7 p-0 text-destructive"
                                onClick={() => handleDeleteTask(task.id)}
                                data-testid={`delete-task-${task.id}`}
                              >
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <p className="text-sm text-muted-foreground text-center py-4">No tasks yet</p>
                )}

                {/* Add Task Form */}
                {addingTaskToZone === zone.id ? (
                  <div className="mt-4 p-4 border-2 border-teal-200 rounded-lg space-y-3">
                    <Input
                      placeholder="New task name"
                      value={newTask.task_name}
                      onChange={(e) => setNewTask({...newTask, task_name: e.target.value})}
                      data-testid={`new-task-name-${zone.id}`}
                    />
                    <div className="flex gap-4">
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="new-task-nightly"
                          checked={newTask.nightly}
                          onCheckedChange={(checked) => setNewTask({...newTask, nightly: checked})}
                          data-testid="new-task-nightly"
                        />
                        <Label htmlFor="new-task-nightly">Nightly</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="new-task-weekly"
                          checked={newTask.weekly}
                          onCheckedChange={(checked) => setNewTask({...newTask, weekly: checked})}
                          data-testid="new-task-weekly"
                        />
                        <Label htmlFor="new-task-weekly">Weekly</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="new-task-monthly"
                          checked={newTask.monthly}
                          onCheckedChange={(checked) => setNewTask({...newTask, monthly: checked})}
                          data-testid="new-task-monthly"
                        />
                        <Label htmlFor="new-task-monthly">Monthly</Label>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" onClick={() => handleCreateTask(zone.id)} data-testid={`save-new-task-${zone.id}`}>
                        Save Task
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => {
                        setAddingTaskToZone(null);
                        setNewTask({ task_name: '', nightly: false, weekly: false, monthly: false });
                      }}>
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : (
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full mt-4"
                    onClick={() => setAddingTaskToZone(zone.id)}
                    data-testid={`add-task-to-zone-${zone.id}`}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add Task to {zone.zone_name}
                  </Button>
                )}
              </CardContent>
            )}
          </Card>
        );
      })}
    </div>
  );
}
